<script setup lang="ts">
import { computed } from 'vue'
import { RouterLink, useRoute } from 'vue-router'
import { useAuthStore } from '../../stores/auth'
import { DISCORD_INVITE_URL } from '../../lib/constants'

const route = useRoute()
const authStore = useAuthStore()

const isActive = (path: string) => route.path === path

const navLinks = computed(() => {
  const links = [
    { path: '/', label: 'Home' },
    { path: '/how-it-works', label: 'How It Works' },
  ]

  if (authStore.isAuthenticated) {
    links.push(
      { path: '/dashboard', label: 'Dashboard' },
      { path: '/rewards', label: 'Rewards' },
      { path: '/servers', label: 'Servers' },
    )
  }

  return links
})

function handleLogin() {
  authStore.login()
}

function handleLogout() {
  authStore.logout()
  window.location.href = '/'
}
</script>

<template>
  <nav class="bg-surface-300 border-b border-surface-100">
    <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div class="flex justify-between h-16">
        <!-- Logo and nav links -->
        <div class="flex items-center">
          <RouterLink to="/">
            <img 
              src="/img/logos/Small_FullTextLogo.png" 
              alt="Chaos Creator" 
              class="h-8"
            />
          </RouterLink>
          
          <div class="hidden md:flex items-center ml-10 gap-6">
            <RouterLink
              v-for="link in navLinks"
              :key="link.path"
              :to="link.path"
              :class="[
                'nav-link',
                isActive(link.path) && 'nav-link-active'
              ]"
            >
              {{ link.label }}
            </RouterLink>
          </div>
        </div>

        <!-- Discord + Auth section -->
        <div class="flex items-center gap-4">
          <a 
            :href="DISCORD_INVITE_URL" 
            target="_blank"
            class="text-gray-400 hover:text-[#5865F2] transition-colors flex items-center gap-1"
            title="Join our Discord"
          >
            <font-awesome-icon :icon="['fab', 'discord']" class="text-lg" />
            <span class="hidden sm:inline">Discord</span>
          </a>
          <template v-if="authStore.isAuthenticated">
            <div class="flex items-center gap-3">
              <img 
                v-if="authStore.user?.avatar_url"
                :src="authStore.user.avatar_url" 
                :alt="authStore.user.display_name"
                class="w-8 h-8 rounded-full"
              />
              <span class="text-gray-300">{{ authStore.user?.display_name }}</span>
            </div>
            <button @click="handleLogout" class="btn btn-secondary text-sm">
              Logout
            </button>
          </template>
          <template v-else>
            <button @click="handleLogin" class="btn btn-primary flex items-center gap-2">
              <font-awesome-icon :icon="['fab', 'twitch']" class="text-lg" />
              Login with Twitch
            </button>
          </template>
        </div>
      </div>
    </div>
  </nav>
</template>
